import { type SyntheticEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { savePinnedDocument } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { Label } from "~/atoms/label";
import { Textarea } from "~/atoms/textarea";
import { ControlledCombobox } from "~/molecules/ControlledCombobox";
import { type DocumentSummary } from "~/api/types";
import { useDocuments } from "~/api/v2/useDocuments";

const DESCRIPTION_MAX_LENGTH = 280;

interface Props {
  open: boolean;
  pinnedDocuments: DocumentSummary[];
  pinnedDocument?: DocumentSummary | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export const PinDocumentDialog = ({
  open,
  pinnedDocuments,
  pinnedDocument = null,
  onOpenChange,
  onSaved,
}: Props) => {
  const { t } = useTranslation();
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [description, setDescription] = useState(
    pinnedDocument?.pin?.description ?? "",
  );
  const [isSaving, setIsSaving] = useState(false);
  const { data: documents, isLoading } = useDocuments();
  const isEditing = pinnedDocument !== null;

  const options = useMemo(
    () =>
      documents
        ?.filter((d1) => !pinnedDocuments.some((d2) => d2.id === d1.id))
        .map((d) => ({
          value: String(d.id),
          label: d.latestRelease.artifact.title,
          subtitle: `.${d.latestRelease.artifact.fileType}`,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    [documents, pinnedDocuments],
  );

  const resetForm = () => {
    setDocumentId(null);
    setDescription("");
  };

  const handleSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isEditing && documentId === null) return;

    setIsSaving(true);
    try {
      if (isEditing) {
        await savePinnedDocument(pinnedDocument.id, description.trim() || null);
        toast.success(t(($) => $.dashboard.pinnedDocuments.toasts.updated));
      } else {
        await savePinnedDocument(
          Number(documentId),
          description.trim() || null,
        );
        toast.success(t(($) => $.dashboard.pinnedDocuments.toasts.added));
      }
      onSaved();
      resetForm();
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsSaving(false);
    }
  };

  const closeDialog = () => {
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) {
          resetForm();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t(($) => $.dashboard.pinnedDocuments.editTitle)
              : t(($) => $.dashboard.pinnedDocuments.addTitle)}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t(($) => $.dashboard.pinnedDocuments.editDescription, {
                  title: pinnedDocument.latestRelease.artifact.title,
                })
              : t(($) => $.dashboard.pinnedDocuments.addDescription)}
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid min-w-0 gap-4"
          onSubmit={(event) => void handleSubmit(event)}
        >
          {isEditing ? (
            <div className="grid min-w-0 gap-2">
              <Label>{t(($) => $.dashboard.pinnedDocuments.document)}</Label>
              <p
                title={pinnedDocument.latestRelease.artifact.title}
                className="border-border bg-muted/40 truncate rounded-lg border px-3 py-2 text-sm"
              >
                {pinnedDocument.latestRelease.artifact.title}
              </p>
            </div>
          ) : (
            <div className="grid min-w-0 gap-2">
              <Label>{t(($) => $.dashboard.pinnedDocuments.document)}</Label>
              <ControlledCombobox
                value={documentId}
                onChange={setDocumentId}
                options={options ?? []}
                disabled={isLoading || isSaving}
                popoverModal
                placeholder={t(
                  ($) => $.dashboard.pinnedDocuments.documentPlaceholder,
                )}
              />
            </div>
          )}
          <div className="grid min-w-0 gap-2">
            <Label htmlFor="pinned-document-description">
              {t(($) => $.dashboard.pinnedDocuments.description)}
            </Label>
            <Textarea
              id="pinned-document-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={DESCRIPTION_MAX_LENGTH}
              disabled={isSaving}
              placeholder={t(
                ($) => $.dashboard.pinnedDocuments.descriptionPlaceholder,
              )}
            />
            <p className="text-muted-foreground text-right text-xs">
              {description.length}/{DESCRIPTION_MAX_LENGTH}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={closeDialog}
            >
              {t(($) => $.commons.buttons.cancel)}
            </Button>
            <Button
              type="submit"
              disabled={(!isEditing && documentId === null) || isSaving}
            >
              {isSaving
                ? t(($) => $.commons.buttons.saving)
                : t(($) => $.commons.buttons.save)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
